import {useNavigate} from 'react-router-dom';
import {ReadCampaign, ReadPage} from '@tinct/core-api-models';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import {ScatterChart, SquareArrowOutUpRight} from 'lucide-react';
import {usePreviewBlockedReason} from '../../../../hooks/use-preview-blocked-reason';
import {PageActionsMenu} from '../../../shared';

export type PageRowActionsProps = {
  page: ReadPage;
  campaign?: ReadCampaign;
};

export function PageRowActions({ page, campaign }: PageRowActionsProps) {
  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();

  const pageId = page.id ?? null;
  const campaignId = campaign?.id ?? null;
  const status = page.status;

  const previewBlockedReason = usePreviewBlockedReason({
    status,
    campaign,
  });
  const canPreview = previewBlockedReason === null;
  const canViewData =
    (status === 'published' || status === 'unpublished') &&
    !!campaignId &&
    !!pageId;

  const previewRoute =
    campaignId && pageId
      ? `${workspaceUrl}/campaigns/${campaignId}/landing-pages/${pageId}/preview`
      : null;

  const handlePreview = () => {
    if (!previewRoute) return;
    window.open(previewRoute, '_blank', 'noopener,noreferrer');
  };

  const handleViewData = () => {
    if (!canViewData) return;
    navigate(`${workspaceUrl}/campaigns/${campaignId}/landing-pages/${pageId}`);
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        disabled={!canViewData}
        onClick={handleViewData}
        aria-label="View variant data"
      >
        <ScatterChart className="size-4" />
      </Button>

      <Tooltip>
        <TooltipTrigger asChild>
          <span className={!canPreview ? 'cursor-not-allowed' : undefined}>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              disabled={!canPreview}
              onClick={handlePreview}
              aria-label="Open internal variant preview in new tab"
            >
              <SquareArrowOutUpRight className="size-4" />
            </Button>
          </span>
        </TooltipTrigger>
        {previewBlockedReason && <TooltipContent>{previewBlockedReason}</TooltipContent>}
      </Tooltip>

      <PageActionsMenu page={page} campaign={campaign} />
    </div>
  );
}
